/**
 * Frontend observability — Web Vitals capture для критических UX SLI.
 *
 * Critical Batch 6/6 (17 May 2026). Service tier `critical UX` — panel
 * единственный admin UI для PADLA, регрессия LCP/INP напрямую бьёт ректора
 * и admin'ов.
 *
 * Capture:
 *   - LCP — Largest Contentful Paint (загрузка viewable contentу)
 *   - INP — Interaction to Next Paint (отзывчивость кликов)
 *   - CLS — Cumulative Layout Shift (визуальная стабильность)
 *   - FCP — First Contentful Paint
 *   - TTFB — Time to First Byte
 *
 * Doc: https://web.dev/articles/vitals
 *
 * Wire: вызвать `initWebVitals()` после `Sentry.init()` в `main.tsx` —
 * метрики уходят как Sentry measurements через `Sentry.setMeasurement(name, value, unit)`
 * либо в `Sentry.metrics.distribution(...)` (v8+ Metrics API).
 *
 * Performance budgets (per CLAUDE.md Принцип XX):
 *   - LCP < 1.5s
 *   - INP < 100ms
 *   - CLS < 0.1
 */

import * as Sentry from '@sentry/react';
import { type Metric, onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

const METRIC_UNITS: Record<string, 'millisecond' | 'none'> = {
  LCP: 'millisecond',
  INP: 'millisecond',
  FCP: 'millisecond',
  TTFB: 'millisecond',
  CLS: 'none',
};

function reportMetric(metric: Metric): void {
  const unit = METRIC_UNITS[metric.name] ?? 'millisecond';
  Sentry.setMeasurement(`web_vitals.${metric.name.toLowerCase()}`, metric.value, unit);
}

/**
 * Инициализирует web-vitals слушатели. Вызывать ОДИН раз, после `Sentry.init()`.
 * Если Sentry не активен (нет DSN) — измерения дойдут до `Sentry.setMeasurement`
 * как no-op (Sentry SDK swallows тихо без отправки).
 */
export function initWebVitals(): void {
  onCLS(reportMetric);
  onFCP(reportMetric);
  onINP(reportMetric);
  onLCP(reportMetric);
  onTTFB(reportMetric);
}
